import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AppContext from '../../Context/AppContext';
import requestAxios from '../../util/requestAxios';
import { guardPortalV2Route } from '../../util/portalV2';
import Sidebar from './Sidebar/Sidebar';
import MetricsStrip from './MetricsStrip/MetricsStrip';
import ActionTrackerCard from './ActionTrackerCard/ActionTrackerCard';
import GoalsCard from './GoalsCard/GoalsCard';
import ELearningStrip from './ELearningStrip/ELearningStrip';
import ELearningHub from './ELearningWorkspace/ELearningHub';
import ELearningWorkspace from './ELearningWorkspace/ELearningWorkspace';
import ActionTrackerWorkspace from './ActionTrackerWorkspace/ActionTrackerWorkspace';
import GoalsWorkspace from './GoalsWorkspace/GoalsWorkspace';
import ProfileWorkspace from './ProfileWorkspace/ProfileWorkspace';
import BadgeEarnedModal from './ProfileWorkspace/BadgeEarnedModal';
import { PROGRAM_IDS } from './ELearningWorkspace/programConfig';
import UserMenu from './UserMenu/UserMenu';
import CoachProfileFinal from '../DashboardCoach/CoachProfileFinal/CoachProfileFinal';
import './DashboardV2.css';
import './DashboardV2.laptop.css';
import './DashboardV2.mobile.css';

function greetingForHour(hour) {
    // Morning 5:00–11:59 · Afternoon 12:00–18:59 · Evening/night 19:00–4:59
    if (hour >= 5 && hour < 12) return 'Good morning';
    if (hour >= 12 && hour < 19) return 'Good afternoon';
    return 'Good evening';
}

function programFromSearch(search) {
    const raw = new URLSearchParams(search).get('program');
    if (!raw) return null;
    const allowed = Object.values(PROGRAM_IDS);
    return allowed.includes(raw) ? raw : null;
}

function tabFromSearch(search) {
    const raw = new URLSearchParams(search).get('tab');
    const allowed = Object.values(PROGRAM_IDS);
    return allowed.includes(raw) ? raw : PROGRAM_IDS.BREAKTHROUGH;
}

function elearnFromSearch(search) {
    return new URLSearchParams(search).get('elearning') === '1';
}

function trackerFromSearch(search) {
    return new URLSearchParams(search).get('tracker') === '1';
}

function goalsFromSearch(search) {
    return new URLSearchParams(search).get('goals') === '1';
}

function profileFromSearch(search) {
    return new URLSearchParams(search).get('profile') === '1';
}

function trackerTabFromSearch(search) {
    const raw = new URLSearchParams(search).get('tab');
    if (raw === 'daily' || raw === 'todo' || raw === 'tracker') return raw;
    return 'tracker';
}

function moduleFromSearch(search) {
    const raw = new URLSearchParams(search).get('module');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
}

function flattenAchievementGroups(achievements) {
    if (!achievements) return [];
    return [
        ...(achievements.badges || []),
        ...(achievements.milestones || []),
        ...(achievements.streaks || []),
    ];
}

function nextUndisplayedBadge(list) {
    return (
        (list || []).find(
            (item) => item && item.achieved === true && item.displayed === false
        ) || null
    );
}

function goalCrushedPreview(count) {
    const n = Math.max(1, Number(count) || 1);
    return {
        id: 8,
        achievementName: `Goal #${n}: Crushed`,
        socialMediaTitle: `Goal #${n}: Crushed`,
        congratsMessage: `Congratulations on crushing goal ${n}! You are one goal closer to your Oola Life.`,
        achieved: true,
        displayed: false,
        achievementValue: String(n),
    };
}

const DashboardV2 = () => {
    const { checkUserSession, changeLoading } = useContext(AppContext);
    const { token, userId } = checkUserSession();
    const history = useHistory();
    const location = useLocation();

    // Soft launch: without usePortalV2 Config flag or /login-v2 session → V1
    useEffect(() => {
        guardPortalV2Route(history, '/dashboard');
    }, [history]);

    const [profile, setProfile] = useState(null);
    const [avatarBust, setAvatarBust] = useState(Date.now());
    const [progress, setProgress] = useState(null);
    const [progressLoading, setProgressLoading] = useState(true);

    const [showElearnHub, setShowElearnHub] = useState(() =>
        elearnFromSearch(location.search)
    );
    const [elearnTab, setElearnTab] = useState(() =>
        tabFromSearch(location.search)
    );
    const [elearnProgram, setElearnProgram] = useState(() =>
        programFromSearch(location.search)
    );
    const [elearnModuleId, setElearnModuleId] = useState(() =>
        moduleFromSearch(location.search)
    );
    const [elearnCourse, setElearnCourse] = useState(null);
    const [fromHub, setFromHub] = useState(
        () => new URLSearchParams(location.search).get('from') === 'hub'
    );
    const [highlightModuleId, setHighlightModuleId] = useState(null);
    // Keep hub mounted while viewing a module so Back is instant (no remount/refetch flash)
    const [hubMounted, setHubMounted] = useState(() =>
        elearnFromSearch(location.search)
    );
    const [showTracker, setShowTracker] = useState(() =>
        trackerFromSearch(location.search)
    );
    const [trackerTab, setTrackerTab] = useState(() =>
        trackerTabFromSearch(location.search)
    );
    const [showGoals, setShowGoals] = useState(() =>
        goalsFromSearch(location.search)
    );
    // Keep Goals workspace mounted after first open so Back / re-open is instant
    const [goalsMounted, setGoalsMounted] = useState(() =>
        goalsFromSearch(location.search)
    );
    const [showProfile, setShowProfile] = useState(() =>
        profileFromSearch(location.search)
    );
    const [profileMounted, setProfileMounted] = useState(() =>
        profileFromSearch(location.search)
    );
    const [earnedBadge, setEarnedBadge] = useState(null);
    const [badgeBusy, setBadgeBusy] = useState(false);

    useEffect(() => {
        changeLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const program = programFromSearch(location.search);
        const hub = elearnFromSearch(location.search);
        const tracker = trackerFromSearch(location.search);
        const goals = goalsFromSearch(location.search);
        const profile = profileFromSearch(location.search);

        if (program) {
            setShowTracker(false);
            setShowGoals(false);
            setShowProfile(false);
            setElearnProgram(program);
            setShowElearnHub(false);
            setElearnModuleId(moduleFromSearch(location.search));
            setFromHub(params.get('from') === 'hub');
            setHubMounted(params.get('from') === 'hub');
            if (params.get('tab')) setElearnTab(tabFromSearch(location.search));
        } else if (hub) {
            setShowTracker(false);
            setShowGoals(false);
            setShowProfile(false);
            setHubMounted(true);
            setShowElearnHub(true);
            setElearnProgram(null);
            setElearnModuleId(null);
            setElearnCourse(null);
            setElearnTab(tabFromSearch(location.search));
            setFromHub(false);
        } else if (tracker) {
            setHubMounted(false);
            setShowTracker(true);
            setShowGoals(false);
            setShowProfile(false);
            setTrackerTab(trackerTabFromSearch(location.search));
            setShowElearnHub(false);
            setElearnProgram(null);
            setElearnModuleId(null);
            setElearnCourse(null);
            setFromHub(false);
        } else if (goals) {
            setHubMounted(false);
            setGoalsMounted(true);
            setShowGoals(true);
            setShowProfile(false);
            setShowTracker(false);
            setShowElearnHub(false);
            setElearnProgram(null);
            setElearnModuleId(null);
            setElearnCourse(null);
            setFromHub(false);
        } else if (profile) {
            setHubMounted(false);
            setProfileMounted(true);
            setShowProfile(true);
            setShowGoals(false);
            setShowTracker(false);
            setShowElearnHub(false);
            setElearnProgram(null);
            setElearnModuleId(null);
            setElearnCourse(null);
            setFromHub(false);
        } else {
            setHubMounted(false);
            setShowTracker(false);
            setShowGoals(false);
            setShowProfile(false);
            setShowElearnHub(false);
            setElearnProgram(null);
            setElearnModuleId(null);
            setElearnCourse(null);
            setFromHub(false);
        }
    }, [location.search]);

    useEffect(() => {
        if (!token) {
            history.push('/');
            return undefined;
        }

        let cancelled = false;

        async function load() {
            setProgressLoading(true);

            try {
                const [profileRes, progressRes] = await Promise.all([
                    requestAxios({
                        url: `Customer/GetCustomerProfileInfo/${userId}`,
                        token,
                    }),
                    requestAxios({
                        url: `Dashboard/GetUserProgress/${userId}`,
                        token,
                    }),
                ]);

                if (cancelled) return;

                if (!profileRes.error && profileRes.data) {
                    setProfile(profileRes.data);
                }
                if (!progressRes.error && progressRes.data) {
                    setProgress(progressRes.data);
                }

                const badgeRes = await requestAxios({
                    url: `Customer/GetMyAchiements/${userId}`,
                    token,
                });
                if (cancelled) return;
                let list = [];
                if (!badgeRes.error && badgeRes.data) {
                    list = Array.isArray(badgeRes.data)
                        ? badgeRes.data
                        : flattenAchievementGroups(
                              badgeRes.data.achievements
                          );
                } else {
                    const profileInfo = await requestAxios({
                        url: `Customer/GetCustomerProfileInformation/${userId}`,
                        token,
                    });
                    if (cancelled) return;
                    if (!profileInfo.error && profileInfo.data) {
                        list = flattenAchievementGroups(
                            profileInfo.data.achievements
                        );
                    }
                }
                setEarnedBadge(nextUndisplayedBadge(list));
            } finally {
                if (!cancelled) {
                    setProgressLoading(false);
                }
            }
        }

        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, userId]);

    const firstName =
        profile?.firstName || checkUserSession()?.firstName || 'there';

    const [hour, setHour] = useState(() => new Date().getHours());
    useEffect(() => {
        const tick = () => setHour(new Date().getHours());
        const id = window.setInterval(tick, 60 * 1000);
        return () => window.clearInterval(id);
    }, []);

    const greeting = `${greetingForHour(hour)}, ${firstName}`;

    const refreshProfile = async () => {
        const { error, data } = await requestAxios({
            url: `Customer/GetCustomerProfileInfo/${userId}`,
            token,
        });
        if (!error && data) {
            setProfile(data);
            setAvatarBust(Date.now());
        }
    };

    const loadPendingBadge = async () => {
        if (!token || !userId) return;
        let list = [];
        const mine = await requestAxios({
            url: `Customer/GetMyAchiements/${userId}`,
            token,
        });
        if (!mine.error && mine.data) {
            list = Array.isArray(mine.data)
                ? mine.data
                : flattenAchievementGroups(mine.data.achievements);
        } else {
            const profileInfo = await requestAxios({
                url: `Customer/GetCustomerProfileInformation/${userId}`,
                token,
            });
            if (!profileInfo.error && profileInfo.data) {
                list = flattenAchievementGroups(profileInfo.data.achievements);
            }
        }
        const next = nextUndisplayedBadge(list);
        setEarnedBadge((prev) => {
            if (!next) return prev;
            if (prev && Number(prev.id) === Number(next.id)) {
                return { ...prev, ...next };
            }
            return next;
        });
    };

    const dismissEarnedBadge = async () => {
        if (badgeBusy) return;
        if (!earnedBadge?.id) {
            setEarnedBadge(null);
            return;
        }
        setBadgeBusy(true);
        await requestAxios({
            url: 'Customer/UpdateDisplayedBadge',
            method: 'put',
            token,
            data: {
                userId,
                achievementId: earnedBadge.id,
            },
        });
        setBadgeBusy(false);
        setEarnedBadge(null);
        await loadPendingBadge();
    };

    const refreshProgress = async () => {
        const progressReq = requestAxios({
            url: `Dashboard/GetUserProgress/${userId}`,
            token,
        });
        const badgeReq = loadPendingBadge();
        const { error, data } = await progressReq;
        if (!error && data) {
            setProgress(data);
        }
        await badgeReq;
        setBadgeBusy(false);
    };

    const previewGoalCrushed = (completedCount) => {
        if (completedCount == null) {
            setEarnedBadge(null);
            setBadgeBusy(false);
            return;
        }
        const n = Math.max(
            1,
            Number(completedCount) || (progress?.goalsCompleted || 0) + 1
        );
        setProgress((prev) =>
            prev
                ? {
                      ...prev,
                      goalsCompleted: Math.max(prev.goalsCompleted || 0, n),
                  }
                : prev
        );
        setEarnedBadge(goalCrushedPreview(n));
        setBadgeBusy(true);
    };

    const openHub = (tab = PROGRAM_IDS.BREAKTHROUGH, highlightId = null) => {
        setShowTracker(false);
        setShowGoals(false);
        setShowProfile(false);
        setHubMounted(true);
        setShowElearnHub(true);
        setElearnProgram(null);
        setElearnModuleId(null);
        setElearnCourse(null);
        setFromHub(false);
        setElearnTab(tab);
        setHighlightModuleId(highlightId);
        history.replace(`/dashboard-v2?elearning=1&tab=${tab}`);
    };

    const openTracker = (tab = 'tracker') => {
        setHubMounted(false);
        setShowTracker(true);
        setShowGoals(false);
        setShowProfile(false);
        setTrackerTab(tab);
        setShowElearnHub(false);
        setElearnProgram(null);
        setElearnModuleId(null);
        setElearnCourse(null);
        setFromHub(false);
        history.replace(`/dashboard-v2?tracker=1&tab=${tab}`);
    };

    const closeTracker = () => {
        setShowTracker(false);
        history.replace('/dashboard-v2');
    };

    const openGoals = () => {
        setHubMounted(false);
        setGoalsMounted(true);
        setShowGoals(true);
        setShowProfile(false);
        setShowTracker(false);
        setShowElearnHub(false);
        setElearnProgram(null);
        setElearnModuleId(null);
        setElearnCourse(null);
        setFromHub(false);
        history.replace('/dashboard-v2?goals=1');
    };

    const closeGoals = () => {
        setShowGoals(false);
        history.replace('/dashboard-v2');
    };

    const openProfile = () => {
        setHubMounted(false);
        setProfileMounted(true);
        setShowProfile(true);
        setShowGoals(false);
        setShowTracker(false);
        setShowElearnHub(false);
        setElearnProgram(null);
        setElearnModuleId(null);
        setElearnCourse(null);
        setFromHub(false);
        history.replace('/dashboard-v2?profile=1');
    };

    const closeProfile = () => {
        setShowProfile(false);
        history.replace('/dashboard-v2');
    };

    const openProgram = (programId) => {
        // Strip / deep links open the new hub on the matching tab
        openHub(programId);
    };

    const openModuleFromHub = (programId, mod) => {
        setHubMounted(true);
        setShowElearnHub(false);
        setElearnProgram(programId);
        setElearnModuleId(mod?.id ?? null);
        setElearnCourse(null);
        setFromHub(true);
        history.replace(
            `/dashboard-v2?program=${programId}&module=${mod.id}&from=hub&tab=${programId}`
        );
    };

    const openProgramFromHub = (programId, course = null) => {
        setHubMounted(true);
        setShowElearnHub(false);
        setElearnProgram(programId);
        setElearnModuleId(null);
        setElearnCourse(course);
        setFromHub(true);
        history.replace(
            `/dashboard-v2?program=${programId}&from=hub&tab=${programId}`
        );
    };

    const closeElearn = () => {
        setShowElearnHub(false);
        setHubMounted(false);
        setElearnProgram(null);
        setElearnModuleId(null);
        setElearnCourse(null);
        setFromHub(false);
        setHighlightModuleId(null);
        history.replace('/dashboard-v2');
    };

    const backFromWorkspace = () => {
        if (fromHub) {
            openHub(
                elearnTab || elearnProgram || PROGRAM_IDS.BREAKTHROUGH,
                elearnModuleId
            );
            return;
        }
        closeElearn();
    };

    const route = useMemo(() => {
        const search = location.search;
        return {
            hub: elearnFromSearch(search),
            program: programFromSearch(search),
            tracker: trackerFromSearch(search),
            goals: goalsFromSearch(search),
            profile: profileFromSearch(search),
            fromHub: new URLSearchParams(search).get('from') === 'hub',
        };
    }, [location.search]);

    const panelHome =
        !route.hub &&
        !route.program &&
        !route.tracker &&
        !route.goals &&
        !route.profile;
    const panelFocus = !panelHome;
    const keepHubCache = route.hub || (route.program && route.fromHub);

    return (
        <div
            className={`dv2${route.hub ? ' dv2--elhub-open' : ''}${
                route.tracker ? ' dv2--tracker-open' : ''
            }`}
        >
            <Sidebar />
            <div className="dv2-main">
                <header
                    className={`dv2-header${
                        panelFocus ? ' dv2-header--compact' : ''
                    }`}
                >
                    {!panelFocus ? (
                        <div>
                            <h1 className="dv2-header__greeting">{greeting}</h1>
                            <p className="dv2-header__sub">
                                Keep taking action. You&apos;re building a life
                                of purpose.
                            </p>
                        </div>
                    ) : (
                        <div />
                    )}
                    <UserMenu
                        profile={profile}
                        avatarBust={avatarBust}
                        onOpenProfile={openProfile}
                    />
                </header>

                {keepHubCache ? (
                    <div
                        className="dv2-elhub-host"
                        hidden={!route.hub}
                        aria-hidden={!route.hub}
                    >
                        <ELearningHub
                            active={route.hub}
                            initialTab={elearnTab}
                            highlightModuleId={highlightModuleId}
                            onBack={closeElearn}
                            onOpenProgram={openProgramFromHub}
                            onOpenModule={openModuleFromHub}
                        />
                    </div>
                ) : null}

                {route.program ? (
                    <ELearningWorkspace
                        programId={route.program}
                        initialModuleId={elearnModuleId}
                        initialCourse={elearnCourse}
                        onBack={backFromWorkspace}
                        onProgressRefresh={refreshProgress}
                    />
                ) : null}

                {route.tracker ? (
                    <ActionTrackerWorkspace
                        initialTab={trackerTab}
                        onBack={closeTracker}
                        onProgressRefresh={refreshProgress}
                    />
                ) : null}

                {goalsMounted ? (
                    <GoalsWorkspace
                        active={route.goals}
                        onBack={closeGoals}
                        onProgressRefresh={refreshProgress}
                        onGoalCompleted={previewGoalCrushed}
                    />
                ) : null}

                {profileMounted ? (
                    <ProfileWorkspace
                        active={route.profile}
                        profile={profile}
                        progress={progress}
                        onBack={closeProfile}
                        onProfileRefresh={refreshProfile}
                    />
                ) : null}

                {panelHome ? (
                    <div className="dv2-home">
                        <MetricsStrip
                            progress={progress}
                            loading={progressLoading}
                        />

                        <div className="dv2-mid">
                            <ActionTrackerCard
                                isActive={panelHome}
                                onProgressRefresh={refreshProgress}
                                onOpenTracker={(tab = 'tracker') =>
                                    openTracker(tab)
                                }
                            />
                            <GoalsCard
                                isActive={panelHome}
                                onProgressRefresh={refreshProgress}
                                onOpenGoals={openGoals}
                            />
                        </div>

                        <ELearningStrip onOpenProgram={openProgram} />
                    </div>
                ) : null}
            </div>
            <BadgeEarnedModal
                badge={earnedBadge}
                goalsCompleted={progress?.goalsCompleted ?? 0}
                busy={badgeBusy}
                onDismiss={dismissEarnedBadge}
                onView={async () => {
                    await dismissEarnedBadge();
                    openProfile();
                }}
            />
            {profile?.isActiveCoach ||
            localStorage.getItem('isActiveCoach') === 'true' ? (
                <CoachProfileFinal userId={userId} />
            ) : null}
        </div>
    );
};

export default DashboardV2;
